import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Mirrors QUIZ_SEGMENTS (somo-circle1-api's src/constants/quizSegments.js)
// — same "hardcode the list in the admin screen" convention
// QuizReportRulesAdd.js already uses for SEGMENTS/NURTURE_TRACKS. Segment
// is the quiz's own classification output, not a raw answer field, so it
// isn't part of the eligibility rule builder below.
const SEGMENT_OPTIONS = [
    { value: 'mental_health', label: 'Mental health' },
    { value: 'financial', label: 'Financial' },
    { value: 'family', label: 'Family' },
    { value: 'all', label: 'All of the above' },
];

// Mirrors somo-circle1-api's src/constants/vaBenefitCriteria.js RULE_OPERATORS.
const OPERATOR_OPTIONS = [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'in', label: 'is one of' },
    { value: 'not_in', label: 'is not one of' },
    { value: 'gte', label: 'is at least (>=)' },
    { value: 'lte', label: 'is at most (<=)' },
    { value: 'gt', label: 'is more than (>)' },
    { value: 'lt', label: 'is less than (<)' },
];
const LIST_OPERATORS = ['in', 'not_in'];
const NUMERIC_OPERATORS = ['gte', 'lte', 'gt', 'lt'];

function emptyRule() {
    return { field: '', operator: 'in', value: [] };
}

function VaBenefitsAdd() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(id ? true : false);
    const [saving, setSaving] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [questionsLoading, setQuestionsLoading] = useState(true);

    const [formData, setFormData] = useState({
        category: '',
        categoryIcon: '',
        title: '',
        description: '',
        applyUrl: 'https://www.va.gov/',
        eligibilityRules: [],
        eligibleSegments: [],
        displayOrder: 0,
        isActive: true,
    });

    // Field dropdown for eligibility rules is populated from the live quiz
    // questions list, not a hardcoded array — so a new question added via
    // the Quiz > Questions screen shows up here automatically, with no
    // code change.
    const fetchQuestions = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/questions/list?limit=200&isActive=true`
            );
            const data = await res.json();
            if (data.status === 'success') {
                setQuestions(data.data.questions || []);
            } else {
                toast.error('Failed to fetch quiz questions for the rule builder');
            }
        } catch (error) {
            console.error('Error fetching quiz questions:', error);
            toast.error('Error fetching quiz questions for the rule builder');
        } finally {
            setQuestionsLoading(false);
        }
    }, []);

    const fetchBenefitDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/va-benefits/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                const benefit = data.data;
                setFormData({
                    category: benefit.category,
                    categoryIcon: benefit.categoryIcon || '',
                    title: benefit.title,
                    description: benefit.description,
                    applyUrl: benefit.applyUrl || 'https://www.va.gov/',
                    eligibilityRules: benefit.eligibilityRules || [],
                    eligibleSegments: benefit.eligibleSegments || [],
                    displayOrder: benefit.displayOrder,
                    isActive: benefit.isActive,
                });
            } else {
                toast.error('Failed to fetch VA benefit details');
            }
        } catch (error) {
            console.error('Error fetching VA benefit:', error);
            toast.error('Error fetching VA benefit details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchQuestions();
        if (id) {
            fetchBenefitDetails();
        }
    }, [id, fetchBenefitDetails, fetchQuestions]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const toggleListValue = (field, value) => {
        setFormData(prev => {
            const current = prev[field];
            const next = current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value];
            return { ...prev, [field]: next };
        });
    };

    const questionByKey = (key) => questions.find((q) => q.questionKey === key);

    const addRule = () => {
        setFormData(prev => ({ ...prev, eligibilityRules: [...prev.eligibilityRules, emptyRule()] }));
    };

    const removeRule = (index) => {
        setFormData(prev => ({
            ...prev,
            eligibilityRules: prev.eligibilityRules.filter((_, i) => i !== index),
        }));
    };

    const updateRule = (index, changes) => {
        setFormData(prev => ({
            ...prev,
            eligibilityRules: prev.eligibilityRules.map((rule, i) => (
                i === index ? { ...rule, ...changes } : rule
            )),
        }));
    };

    const handleRuleFieldChange = (index, questionKey) => {
        // Reset the value when the field changes — an option code picked
        // for one question is almost never valid for another.
        updateRule(index, { field: questionKey, value: LIST_OPERATORS.includes(formData.eligibilityRules[index].operator) ? [] : '' });
    };

    const handleRuleOperatorChange = (index, operator) => {
        updateRule(index, { operator, value: LIST_OPERATORS.includes(operator) ? [] : '' });
    };

    const handleRuleListValueToggle = (index, optionValue) => {
        const rule = formData.eligibilityRules[index];
        const current = Array.isArray(rule.value) ? rule.value : [];
        const next = current.includes(optionValue)
            ? current.filter((v) => v !== optionValue)
            : [...current, optionValue];
        updateRule(index, { value: next });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.category.trim() || !formData.title.trim() || !formData.description.trim()) {
            toast.error('Category, Title, and Description are required');
            return;
        }

        try {
            setSaving(true);

            const incompleteRule = formData.eligibilityRules.find((rule) => (
                !rule.field || (Array.isArray(rule.value)
                    ? rule.value.length === 0
                    : rule.value === '' || rule.value === undefined || rule.value === null)
            ));
            if (incompleteRule) {
                toast.error('Every eligibility rule needs a field, operator, and value — remove any unfinished rows');
                return;
            }

            const payload = {
                ...formData,
                category: formData.category.trim(),
                title: formData.title.trim(),
                description: formData.description.trim(),
                displayOrder: parseInt(formData.displayOrder, 10) || 0,
            };

            const url = id
                ? `${Base_Url}/admin/quiz/va-benefits/${id}`
                : `${Base_Url}/admin/quiz/va-benefits`;

            const method = id ? 'PUT' : 'POST';

            const res = await authManager.authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.status === 'success') {
                toast.success(id ? 'Benefit updated successfully!' : 'Benefit created successfully!');
                setTimeout(() => navigate('/vaBenefits'), 1500);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (err) {
            console.error('Error saving VA benefit:', err);
            toast.error('Error saving VA benefit');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">{id ? 'Edit' : 'Add'} VA Benefit</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/vaBenefits">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Category *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="category"
                                                            value={formData.category}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., Disability, Healthcare, Housing"
                                                        />
                                                        <small className="text-muted">
                                                            Benefits with the same category are grouped together on the report PDF.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Title *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="title"
                                                            value={formData.title}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., VA Disability Compensation"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Description *</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className="form-control"
                                                            name="description"
                                                            rows="3"
                                                            value={formData.description}
                                                            onChange={handleInputChange}
                                                            placeholder="Plain-language explanation of the benefit"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Apply URL</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="applyUrl"
                                                            value={formData.applyUrl}
                                                            onChange={handleInputChange}
                                                            placeholder="https://www.va.gov/..."
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Eligibility Rules</label>
                                                    <div className="col-12 col-md-9">
                                                        {questionsLoading ? (
                                                            <small className="text-muted">Loading quiz questions...</small>
                                                        ) : (
                                                            <>
                                                                {formData.eligibilityRules.map((rule, index) => {
                                                                    const question = questionByKey(rule.field);
                                                                    const isListOp = LIST_OPERATORS.includes(rule.operator);
                                                                    const isNumericOp = NUMERIC_OPERATORS.includes(rule.operator);
                                                                    return (
                                                                        <div className="card mb-2" key={index}>
                                                                            <div className="card-body py-2">
                                                                                <div className="row align-items-center">
                                                                                    <div className="col-md-4 mb-2">
                                                                                        <select
                                                                                            className="form-control form-control-sm"
                                                                                            value={rule.field}
                                                                                            onChange={(e) => handleRuleFieldChange(index, e.target.value)}
                                                                                        >
                                                                                            <option value="">Select a quiz question...</option>
                                                                                            {questions.map((q) => (
                                                                                                <option key={q.questionKey} value={q.questionKey}>
                                                                                                    {q.questionText}
                                                                                                </option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                    <div className="col-md-3 mb-2">
                                                                                        <select
                                                                                            className="form-control form-control-sm"
                                                                                            value={rule.operator}
                                                                                            onChange={(e) => handleRuleOperatorChange(index, e.target.value)}
                                                                                        >
                                                                                            {OPERATOR_OPTIONS.map((opt) => (
                                                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                    <div className="col-md-4 mb-2">
                                                                                        {isListOp && question?.options?.length > 0 ? (
                                                                                            question.options.map((opt) => (
                                                                                                <div className="form-check form-check-inline" key={opt.value}>
                                                                                                    <input
                                                                                                        className="form-check-input"
                                                                                                        type="checkbox"
                                                                                                        id={`rule-${index}-${opt.value}`}
                                                                                                        checked={Array.isArray(rule.value) && rule.value.includes(opt.value)}
                                                                                                        onChange={() => handleRuleListValueToggle(index, opt.value)}
                                                                                                    />
                                                                                                    <label className="form-check-label" htmlFor={`rule-${index}-${opt.value}`}>
                                                                                                        {opt.label}
                                                                                                    </label>
                                                                                                </div>
                                                                                            ))
                                                                                        ) : isListOp && question ? (
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control form-control-sm"
                                                                                                value={Array.isArray(rule.value) ? rule.value.join(', ') : ''}
                                                                                                onChange={(e) => updateRule(index, {
                                                                                                    value: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
                                                                                                })}
                                                                                                placeholder="Comma-separated values (this question has no fixed options)"
                                                                                            />
                                                                                        ) : isListOp ? (
                                                                                            <small className="text-muted">Pick a question first</small>
                                                                                        ) : (
                                                                                            <input
                                                                                                type={isNumericOp ? 'number' : 'text'}
                                                                                                className="form-control form-control-sm"
                                                                                                value={rule.value}
                                                                                                onChange={(e) => updateRule(index, {
                                                                                                    value: isNumericOp ? Number(e.target.value) : e.target.value,
                                                                                                })}
                                                                                                placeholder={question && !isListOp ? 'Enter a value to match' : 'Pick a question first'}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="col-md-1 mb-2 text-right">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-link text-danger p-0"
                                                                                            onClick={() => removeRule(index)}
                                                                                        >
                                                                                            <i className="fa-solid fa-trash-can" title="Remove rule" />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addRule}>
                                                                    <i className="fa-solid fa-plus mr-1" /> Add rule
                                                                </button>
                                                            </>
                                                        )}
                                                        <small className="text-muted d-block mt-2">
                                                            A lead's quiz answers must match every rule here to show "Likely Eligible" for
                                                            this benefit. Leave rules empty if this can't be determined from the quiz's own
                                                            answers — it'll always show "Possibly Eligible."
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Quiz Segments</label>
                                                    <div className="col-12 col-md-9">
                                                        {SEGMENT_OPTIONS.map((opt) => (
                                                            <div className="form-check form-check-inline" key={opt.value}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`seg-${opt.value}`}
                                                                    checked={formData.eligibleSegments.includes(opt.value)}
                                                                    onChange={() => toggleListValue('eligibleSegments', opt.value)}
                                                                />
                                                                <label className="form-check-label" htmlFor={`seg-${opt.value}`}>
                                                                    {opt.label}
                                                                </label>
                                                            </div>
                                                        ))}
                                                        <small className="text-muted d-block mt-1">
                                                            Which quiz segments this benefit appears for. Leave all unchecked to show it
                                                            for every segment.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Display Order</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="displayOrder"
                                                            value={formData.displayOrder}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                        />
                                                        <small className="text-muted">Lower numbers appear first within their category</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Active</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="isActive"
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="isActive">
                                                                {formData.isActive ? 'Shown on the report PDF' : 'Hidden from the report PDF'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-footer text-center">
                                                <button
                                                    type="submit"
                                                    className="btn btn-blue"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default VaBenefitsAdd
